// Shared display helpers for the Dispute Console (DisputeConsolePage +
// DisputeOpsPanel) — kept together so the table and the drawer never drift on
// how a status/amount/date renders.

// Mirrors o2c-backend's DisputeStatus enum (database/entities/Dispute.ts).
// `cls` keys a color in the "Dispute Console" section of App.css.
export const DISPUTE_STATUS_META = {
  DISPUTE_PENDING: { label: 'Queued for raising ticket', cls: 'disp-st-file' },
  AWAITING_PLATFORM_RESPONSE: { label: 'Awaiting Platform Response', cls: 'disp-st-portal' },
  AWAITING_BRAND_RESPONSE: { label: 'Awaiting Brand Response', cls: 'disp-st-brand' },
  QUEUED_FOR_UPDATE: { label: 'Queued for platform submission', cls: 'disp-st-post' },
  DONE: { label: 'Closed', cls: 'disp-st-done' },
};

// A dispute's real status label. Deliberately ignores `outcome` — closing a
// dispute here only means the platform said it's settled, not a confirmed
// win/loss (that's only known later, once reconciliation data confirms it),
// so the UI never distinguishes "resolved" from "closed – rejected".
export const disputeStatusLabel = (status) => {
  return DISPUTE_STATUS_META[status]?.label || status;
};

// Tabs in board order — `action: true` marks the two states that need the
// ops team to actually do something (highlighted in the tab strip).
export const DISPUTE_TABS = [
  { key: 'ALL', label: 'All Open' },
  { key: 'DISPUTE_PENDING', label: 'Raise tickets', action: true },
  { key: 'QUEUED_FOR_UPDATE', label: 'Submit brand responses', action: true },
  { key: 'FOLLOW_UP_DUE', label: 'Platform follow-ups', action: true },
  { key: 'AWAITING_PLATFORM_RESPONSE', label: 'Waiting on platform' },
  { key: 'AWAITING_BRAND_RESPONSE', label: 'Waiting on brand' },
  { key: 'DONE', label: 'Completed' },
];

// Mirrors the issue_type values on invoice_action_tickets (see workspace
// CLAUDE.md's "Reconciliation Business Rules").
const ISSUE_TYPE_LABELS = {
  grn_missing: 'No GRN',
  grn_short: 'Short GRN',
  unbooked_invoice: 'Unbooked Invoice',
  payment_overdue: 'Payment Overdue',
};
export const formatIssueType = (issueType) => ISSUE_TYPE_LABELS[issueType] || issueType || '—';

export const slaClass = (state) => (
  state === 'breach' ? 'disp-sla-breach' : state === 'warn' ? 'disp-sla-warn' : 'disp-sla-ok'
);

export const fmtCompactINR = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${v.toLocaleString('en-IN')}`;
};
export const fmtFullINR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

export const fmtDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
export const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Deterministic brand-initial avatar color — real brand names aren't a fixed
// list (unlike the design mockup's hardcoded BRANDS map), so hash into a
// small fixed palette instead of assigning colors by hand.
const AVATAR_PALETTE = ['#2B7A6B', '#E11D48', '#0EA5E9', '#7C3AED', '#B45309', '#0891B2', '#DB2777', '#059669', '#D97706', '#4338CA'];
export const brandColor = (name) => {
  let hash = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};
export const brandInitials = (name) => (name || '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
